import { FileText, Mic, Trash2 } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useOrgDateFormat } from '@/lib/timezone';
import type { UploadedDocumentOut } from '../types';
import { formatCalendarDate } from '@/lib/utils/date';
import { DocumentTagPicker } from './DocumentTagPicker';
import { resolveGeneratedDocumentTitle } from '../utils';
import { AssignDocumentPopover } from './AssignDocumentPopover';
import type { GeneratedDocumentOut } from '../api/documents-api';
import { documentStatusColors, documentStatusIcons } from '../constants';

export type InboxItem =
  | { kind: 'generated'; data: GeneratedDocumentOut }
  | { kind: 'uploaded'; data: UploadedDocumentOut };

export interface InboxRowProps {
  /** Disabled while a delete or assign mutation is in flight. */
  busy?: boolean;
  item: InboxItem;
  onAssign: (item: InboxItem, patientId: string) => void;
  onDelete: (item: InboxItem) => void;
  onOpen: (item: InboxItem) => void;
}

interface InboxRowVisual {
  dateLabel: string;
  glyph: ReactNode;
  sourcePill: string;
  statusIcon: ComponentType<{ className?: string }> | null;
  title: string;
}

/**
 * One Inbox row representing either a generated or uploaded orphan document.
 *
 * Renders source pill, primary title, tag chips (via the existing
 * `DocumentTagPicker`), and per-row Assign / Delete actions. Plan 06.
 */
export function InboxRow({ item, busy, onOpen, onAssign, onDelete }: InboxRowProps) {
  const { formatInstant } = useOrgDateFormat();
  const visual = visualForItem(item, formatInstant);
  const StatusIcon = visual.statusIcon;

  return (
    <div className="grid grid-cols-[auto_1fr_auto] items-start gap-4 rounded-lg border bg-card p-4 hover:bg-muted/30 transition-colors">
      {/* Glyph */}
      <button
        type="button"
        aria-label={`Open ${visual.title}`}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
        onClick={() => onOpen(item)}>
        {visual.glyph}
      </button>

      {/* Title + meta + tags */}
      <div className="min-w-0 space-y-2">
        <button type="button" className="block text-left w-full" onClick={() => onOpen(item)}>
          <p className="text-sm font-semibold truncate">{visual.title}</p>
          <p className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
            <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
              {visual.sourcePill}
            </Badge>
            <span>{visual.dateLabel}</span>
            {item.kind === 'generated' && (
              <span className="inline-flex items-center gap-1">
                <span className="text-border">·</span>
                {StatusIcon && <StatusIcon className="h-3 w-3" />}
                <Badge
                  variant="secondary"
                  className={`text-[10px] capitalize ${documentStatusColors[item.data.status] ?? ''}`}>
                  {item.data.status}
                </Badge>
              </span>
            )}
          </p>
        </button>

        {item.kind === 'uploaded' && item.data.source?.provider === 'srfax' && (
          <p className="text-xs text-muted-foreground">
            From {item.data.source.sender || 'Unknown sender'} · {item.data.source.pages} pages
            {item.data.source.received_at_raw && ` · Received ${item.data.source.received_at_raw} (SRFax account time)`}
          </p>
        )}
        {/* Inline tag picker — reuses Plan 02's DocumentTagPicker. */}
        <DocumentTagPicker kind={item.kind} documentId={item.data.id} />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5">
        <AssignDocumentPopover disabled={busy} onAssign={(patient) => onAssign(item, patient.id)} />
        <Button size="icon" disabled={busy} variant="ghost" aria-label="Delete document" onClick={() => onDelete(item)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function visualForItem(item: InboxItem, formatInstant: (iso: string, pattern: string) => string): InboxRowVisual {
  if (item.kind === 'generated') {
    const doc = item.data;
    const statusIcon = documentStatusIcons[doc.status] ?? FileText;
    return {
      statusIcon,
      sourcePill: 'Quick Start',
      title: resolveGeneratedDocumentTitle(doc),
      glyph: <Mic aria-hidden className="h-5 w-5" />,
      dateLabel: formatInstant(doc.generated_at, 'MMM d, yyyy · HH:mm'),
    };
  }
  const doc = item.data;
  return {
    title: doc.name,
    statusIcon: null,
    dateLabel: formatCalendarDate(doc.document_date),
    glyph: <FileText aria-hidden className="h-5 w-5" />,
    sourcePill: doc.source?.provider === 'srfax' ? 'Fax' : 'Upload',
  };
}
